import {Component, EventEmitter, Input, OnInit, Output, SimpleChanges} from '@angular/core';
import {FormControl, FormGroup, ValidatorFn, Validators} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {Store} from '@ngrx/store';
import {Subject} from 'rxjs/Subject';
import {takeUntil} from 'rxjs/operators';
import {ShipmentService} from '@services/shipment.service';
import {UserService} from '@services/user.service';
import {FontAwesomeService} from '@services/font-awesome.service';
import {AppState, CountriesState} from '@store/reducers/index';
import {User} from '@models/user.interface';
import {selectActiveSearchId, selectUser} from '@store/reducers';
import {AddressService} from '@services/address.service';
import 'rxjs/add/operator/map';

interface Country {
  value: string;
  label: string;
}

@Component({
  selector: 'app-consignment-form',
  templateUrl: './consignment-form.component.html',
  styleUrls: ['./consignment-form.component.scss']
})

export class ConsignmentFormComponent implements OnInit {
  showFields = false;
  possibleCountries: Array<Country>;
  user: User;
  isReseting: boolean;
  updateCountryRules: EventEmitter<{ fromCountry?: string, toCountry?: string }> = new EventEmitter();
  addresses: Array<Country>;
  autocompleteAddresses: Array<{ label: string, address: string, postcode: string, city: string }>;
  showSavedCheckbox = true;
  private unsubscribe: Subject<void> = new Subject();

  public showPickupPart = false;
  public showDeliveryPart = false;

  @Input() parent: FormGroup;
  @Input() from: boolean;
  @Input() countryRules: {
    from: Array<Country>,
    to: Array<Country>
  };
  @Input() title: string;

  @Input() partType: string;
  @Input() submitted: boolean;
  @Input() currentSearchIdSaved: number | null;
  @Output() updateFormData: EventEmitter<any> = new EventEmitter();

  constructor(
    private translate: TranslateService,
    private shipmentService: ShipmentService,
    private userService: UserService,
    private addressService: AddressService,
    public faService: FontAwesomeService,
    private store: Store<AppState>
  ) {
  }

  ngOnInit() {
    // add fields that depends on this
    // this.parent.addControl(this.from ? 'pickup_instructions' : 'delivery_instructions', new FormControl('123'));

    // if (!this.from) {
    //   this.parent.addControl('send_tracking_information', new FormControl(false));
    // }
    if (this.parent.get('type').value === 'private') {
      this.parent.removeControl('company_name');
      this.addValidation();
    } else {
      this.removeValidation();
    }

    /*
    this.parent.get('address').valueChanges.merge(
      this.parent.get('city').valueChanges,
      this.parent.get('country_code').valueChanges,
      this.parent.get('zip').valueChanges
    )
      .debounceTime(4000)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(() => this.search());
    this.search();
    */

    this.parent.get('type').valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(type => {
        const controls = ['name', 'surname', 'phone', 'email'];
        if (type === 'private') {
          this.addValidation();
          this.parent.removeControl('company_name');
        } else if (type === 'company') {
          this.removeValidation();
          this.parent.addControl('company_name', new FormControl('', [Validators.required]));
        }
      });

    this.store.select('countries')
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((data: CountriesState) => {
        this.possibleCountries = data.possibleCountries;
      });

    this.store.select(selectUser)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((data: User) => {
        this.user = data;
        /*
        this.userService.getAddressBooks()
          .subscribe(addressBooks => {
            const addresses = Array.isArray(addressBooks) ? addressBooks : addressBooks.data;
            this.addresses = addresses.map(address => ({...address, value: address.id, label: address.alias}));
          });
          */
      });

    this.userService.getAddressBooks()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(addressBooks => {
        const addresses = Array.isArray(addressBooks) ? addressBooks : addressBooks.data;
        this.addresses = addresses.map(address => ({...address, value: address.id, label: address.alias}));
      });

    this.parent.get('alias').valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .debounceTime(500)
      .distinctUntilChanged()
      .switchMap(filter => {
        const params = {filter, page: 0, size: 10};
        return this.userService.getAddressBooks(params);
      })
      .subscribe((addressBooks) => {
        const addresses = Array.isArray(addressBooks) ? addressBooks : addressBooks.data;
        this.addresses = addresses.map(address => ({...address, value: address.id, label: address.alias}));
      });

    // Address autocomplete
    this.parent.get('address').valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .debounceTime(500)
      .distinctUntilChanged()
      .subscribe((query) => {
        this.autocompleteAddresses = [];
        if (query.length > 3) {
          const countryCode = this.parent.get('country_code').value;
          if (this.addressService.validCountryCodes.includes(countryCode)) {
            this.addressService.autocomplete(countryCode, query)
              .pipe(takeUntil(this.unsubscribe))
              .subscribe(autocomplete => {
                this.autocompleteAddresses = autocomplete;
              });
          }
        }
      });

    this.store.select(selectActiveSearchId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(id => {
        if (this.parent.get('is_used').value === true) {
          this.showForm();
        }
        this.showFields = !!id;
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.submitted && changes.submitted.currentValue) {
      this.showFields = true;
    }
    // this.showSavedCheckbox = !this.currentSearchIdSaved;
  }

  private addValidation() {
    this.parent.get('name').setValidators([Validators.required]);
    this.parent.get('name').updateValueAndValidity();
    this.parent.get('surname').setValidators([Validators.required]);
    this.parent.get('surname').updateValueAndValidity();
    this.parent.get('phone').setValidators([Validators.required]);
    this.parent.get('phone').updateValueAndValidity();
    this.parent.get('email').setValidators([Validators.required, Validators.email]);
    this.parent.get('email').updateValueAndValidity();
  }

  private removeValidation() {
    this.parent.get('name').clearValidators();
    this.parent.get('name').updateValueAndValidity();
    this.parent.get('surname').clearValidators();
    this.parent.get('surname').updateValueAndValidity();
    this.parent.get('phone').clearValidators();
    this.parent.get('phone').updateValueAndValidity();
    this.parent.get('email').clearValidators();
    this.parent.get('email').updateValueAndValidity();
  }

  setData(data, isCompany) {
    this.resetData();
    if (!data) {
      return;
    }
    const {
      address, zip, country, country_code, city, name, surname, phone, email,
      instructions, door_code, company_name, send_tracking_information
    } = data;
    this.parent.patchValue({
      type: isCompany ? 'company' : 'private',
      address,
      zip,
      city,
      country: country || this.getCountryByCountryCode(country_code),
      country_code: country_code || this.getCountryCodeByCountry(country),
      company_name: isCompany && company_name,
      name: name,
      surname: surname,
      phone,
      email,
      instructions: instructions,
      door_code: door_code,
      send_tracking_information: send_tracking_information,
    });
    // this.showSavedCheckbox = !data.id;
    this.showFields = true;
  }

  getCountryCodeByCountry(countryName) {
    const country: any = this.possibleCountries.find(({label}) => label === countryName);
    return country && country.value;
  }

  getCountryByCountryCode(countryCode) {
    const country: any = this.possibleCountries.find(({value}) => value === countryCode);
    return country && country.label;
  }

  onChange(event) {
    event ? this.setData(event, event.company) : this.resetData();
  }

  useUserData() {
    this.setData(this.user, this.user.is_company);
  }

  limitCountries(country: Country) {
    let data: { fromCountry?: string, toCountry?: string };
    if (country) {
      data = this.from ? {fromCountry: country.value} : {toCountry: country.value};
    } else {
      data = {fromCountry: null, toCountry: null};
    }
    this.updateCountryRules.emit(data);
  }

  static generateForm() {
    const generateControl = (initialValue = null, validators: ValidatorFn[] = []) =>
      ConsignmentFormComponent.generateFormControl(initialValue, validators);
    return new FormGroup({
      is_used: new FormControl(false),
      alias: new FormControl(''),
      type: generateControl('company'),
      address: generateControl(''),
      zip: generateControl('', [Validators.minLength(3)]),
      city: generateControl(''),
      country: generateControl(''),
      country_code: generateControl(''),
      // place_id: generateControl(''),
      company_name: generateControl(''),
      name: generateControl(''),
      surname: generateControl(''),
      phone: generateControl('', [Validators.required]),
      email: generateControl(''),
      instructions: new FormControl(''),
      door_code: new FormControl(''),
      save_address: new FormControl(false),
      send_tracking_information: new FormControl(false)
    });
  }

  static validatePhone(control: FormControl) {
    const pattern = /^[0-9\-\+\ \(\)]+/g;
    if (control !== undefined && !pattern.test(control.value)) {
      return {pattern: true};
    }
    return null;
  }

  static validateEmail(control: FormControl) {
    const pattern = /^[a-zA-Z0-9\.\-\+'_]+@[a-zA-Z0-9]+(\-[a-zA-Z0-9]+|\.[a-zA-Z0-9]+)*\.[a-zA-Z]{2,6}$/i;
    if (control !== undefined && !pattern.test(control.value)) {
      return {pattern: true};
    }
    return null;
  }

  static generateFormControl(initialValue, validators) {
    return new FormControl(initialValue, [Validators.required, ...validators]);
  }

  /*
  search() {
    this.mapsAPILoader.load().then(() => {
      this.geocoder = new google.maps.Geocoder();
      const address = `${this.parent.get('address').value}, ${this.parent.get('zip').value}
      ${this.parent.get('city').value} ${this.parent.get('country_code').value}`;
      this.geocoder.geocode(
        {
          address,
          componentRestrictions: {country: this.parent.get('country_code').value}
        },
        (results, status) => {
          if (status === google.maps.GeocoderStatus.OK) {
            console.log('set place ID');
            this.parent.get('place_id').setValue(results[0].place_id);
          } else {
            console.log('place ID not found');
            this.parent.get('place_id').setValue('');
          }
        });
    });

  }
  */

  /*
  get instructionsFormControl() {
    return `${this.from ? 'pickup_' : 'delivery_'}instructions`;
  }
  */

  get instructionsPlaceholder() {
    // return `${this.from ? 'Pickup' : 'Delivery'} instructions`;
    return this.translate.instant(
      this.from
        ? 'account.section.createConsignment.form.consignment.pickupInstructions'
        : 'account.section.createConsignment.form.consignment.deliveryInstructions'
    );
  }

  get isPerson() {
    return this.parent.get('type').value === 'private';
  }

  enterAddressManually() {
    this.showFields = true;
    this.resetData();
  }

  resetButtonClicked() {
    this.resetData();
    this.parent.get('is_used').setValue(false);
    if (this.partType === 'pickup') {
      this.showPickupPart = false;
    } else if (this.partType === 'delivery') {
      this.showDeliveryPart = false;
    }
  }

  resetData() {
    this.isReseting = true;
    // this.showSavedCheckbox = true;
    this.parent.reset({
      is_used: this.parent.get('is_used').value,
      country_code: 'SE',
      country: 'Sweden',
    }, {emitEvent: false});
    this.parent.get('type').setValue('company');
  }

  setIsReseting() {
    if (this.isReseting) {
      this.isReseting = false;
    }
  }

  showForm() {
    this.parent.get('is_used').setValue(true);
    if (this.partType === 'pickup') {
      this.showPickupPart = true;
    } else if (this.partType === 'delivery') {
      this.showDeliveryPart = true;
    }
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

}




// WEBPACK FOOTER //
// ./src/app/modules/consignment/search/components/consignment-form/consignment-form.component.ts